import { Card, Form, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { CheckCircleFill, Trash } from "react-bootstrap-icons";
import { IntegrationStatus, SubscriptionStatus } from "./Data/ApiTransport";
import { Vehicle } from "./Data/ApiTypes";
import { getStateEnumFromCode, showStateName } from "./Data/UsState";
import { getVehicleIntegrationDisplayName } from "./Data/IntegrationDisplay";
import {
  ServerRequestsParkerIntegrationPartyDto,
} from "./shared/api/axios-client";
import { CarFrontFill } from "react-bootstrap-icons";
import { supportsRealTimeSyncCheck } from "./Data/IntegrationCapabilities";
import { Button } from "./Controls/Button";
import {
  vehicleIntegrationSyncKey,
  VehicleSyncState,
} from "./VehiclesSection";

interface ReservationVehicleCardProps {
  vehicle: Vehicle;
  onRemove: (vehicle: Vehicle) => void;
  subscriptionStatus: SubscriptionStatus;
  integrations: ServerRequestsParkerIntegrationPartyDto[];
  subscriptionId: number;
  facilityId: number;
  syncStates: Record<string, VehicleSyncState>;
  resyncingKey: string | null;
  onResync: (integration: ServerRequestsParkerIntegrationPartyDto) => void;
  setPendingToggle: (data: {
    integrationPartyId: number;
    subscriptionId: number;
    parkerId: number;
    facilityId: number;
    newStatus: IntegrationStatus;
    vehicleId: number;
  }) => void;
  setShowToggleConfirm: (show: boolean) => void;
}

export function ReservationVehicleCard({
  vehicle,
  onRemove,
  subscriptionStatus,
  integrations,
  subscriptionId,
  facilityId,
  syncStates,
  resyncingKey,
  onResync,
  setPendingToggle,
  setShowToggleConfirm,
}: ReservationVehicleCardProps) {
  const vehicleIntegrations = integrations.filter(
    (x) => x.vehicleId !== undefined && x.vehicleId === vehicle.vehicleId
  );

  const canSyncCheck = (integration: ServerRequestsParkerIntegrationPartyDto) =>
    supportsRealTimeSyncCheck(integration) && integration.isActive;

  const renderVerifyStatus = (
    integration: ServerRequestsParkerIntegrationPartyDto
  ) => {
    if (
      !canSyncCheck(integration) ||
      integration.integrationPartyId == null ||
      integration.vehicleId == null
    ) {
      return null;
    }

    const key = vehicleIntegrationSyncKey(
      integration.integrationPartyId,
      integration.vehicleId
    );
    const state = syncStates[key];
    if (!state) return null;

    if (state.status === "verifying") {
      return (
        <span
          className="d-inline-flex align-items-center gap-1 small text-muted"
          style={{ whiteSpace: "nowrap" }}
        >
          <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
          Verifying
        </span>
      );
    }

    if (state.status === "verified") {
      const verifiedIcon = (
        <span
          className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            backgroundColor: "color-mix(in srgb, #2f7d32 12%, white)",
            color: "#2f7d32",
            cursor: "default",
          }}
        >
          <CheckCircleFill size={14} />
        </span>
      );

      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`vehicle-verified-${key}`}>
              Vehicle confirmed active in the access system
            </Tooltip>
          }
        >
          {verifiedIcon}
        </OverlayTrigger>
      );
    }

    const problemLabel =
      state.status === "error" ? "Check failed" : "Not verified";

    const problemTooltip = state.canResync
      ? state.message ??
        (state.status === "error"
          ? "Could not verify vehicle with the access system"
          : "Vehicle was not found or plate/state does not match in the access system")
      : [state.message, "Contact support to resolve this issue."]
          .filter(Boolean)
          .join(" ");

    const problemBadge = (
      <span
        className="badge rounded-pill fw-normal"
        style={{
          backgroundColor: "color-mix(in srgb, #f0ad4e 18%, white)",
          color: "#8a5a00",
          cursor: "default",
        }}
      >
        {problemLabel}
      </span>
    );

    return (
      <div className="d-inline-flex align-items-center gap-2 flex-wrap">
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`vehicle-not-verified-${key}`}>{problemTooltip}</Tooltip>
          }
        >
          {problemBadge}
        </OverlayTrigger>
        {state.canResync && (
          <Button
            className="btn btn-sm py-0 px-2"
            disabled={resyncingKey === key}
            onClick={() => onResync(integration)}
          >
            Resync
          </Button>
        )}
      </div>
    );
  };

  return (
    <Card className="mb-2 border">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-2 border border-secondary d-flex align-items-center justify-content-center"
              style={{
                width: "32px",
                height: "32px",
                backgroundColor:
                  vehicle.color === "black"
                    ? "#f8f9fa"
                    : vehicle.color === "White"
                    ? "#343a40"
                    : "",
              }}
            >
              <CarFrontFill
                style={{
                  width: "20px",
                  height: "20px",
                  color: vehicle.color,
                }}
              />
            </div>

            <div>
              <div className="fw-bold">
                {vehicle.make} {vehicle.model}
              </div>
              <div className="text-muted small">
                {showStateName(getStateEnumFromCode(vehicle.stateCode)!)}{" "}
                {vehicle.licensePlateNumber}
              </div>
            </div>
          </div>

          {(subscriptionStatus === SubscriptionStatus.Active ||
            subscriptionStatus === SubscriptionStatus.Applied) && (
            <button
              className="btn btn-sm"
              title="Delete Vehicle"
              onClick={() => onRemove(vehicle)}
            >
              <Trash />
            </button>
          )}
        </div>

        {vehicleIntegrations.length > 0 && (
          <div className="mt-3">
            {vehicleIntegrations.map((integration) => (
              <div
                key={`${integration.integrationPartyId}-${
                  integration.vehicleId ?? vehicle.vehicleId
                }`}
                className="d-flex justify-content-between align-items-center flex-wrap gap-2 border rounded p-2 mb-2"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <span className="fw-bold small">
                    {getVehicleIntegrationDisplayName(integration.integrationName)}
                  </span>
                  {renderVerifyStatus(integration)}
                </div>

                {setPendingToggle && setShowToggleConfirm && (
                  <Form.Check
                    type="switch"
                    id={`integration-toggle-${integration.integrationPartyId}-${
                      integration.vehicleId ?? vehicle.vehicleId
                    }`}
                    checked={integration.isActive || false}
                    onChange={() => {
                      setPendingToggle({
                        integrationPartyId: integration.integrationPartyId!,
                        subscriptionId: subscriptionId,
                        parkerId: integration.parkerId!,
                        vehicleId: integration.vehicleId ?? vehicle.vehicleId,
                        facilityId: facilityId,
                        newStatus: integration.isActive
                          ? IntegrationStatus.Deactivated
                          : IntegrationStatus.Activated,
                      });
                      setShowToggleConfirm(true);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
